import axios from "axios";
import { getValidToken } from "./authUtils";

export const verifyPaymentWithTimeout = async (paymentResponse: any) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const token = await getValidToken();
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}payments/razorpay/verify`,
      {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        timeout: 25000,
      }
    );

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export const verifyPayment = async (
  paymentResponse: any,
  companyId: string,
  incrementDays: number = 30
) => {
  try {
    const response = await verifyPaymentWithTimeout(paymentResponse);

    if (!response.data?.isSuccess) {
      throw new Error(response.data?.message || "Payment verification failed");
    }

    await updateCompanyPlanStatus(companyId, incrementDays);
    return response;
  } catch (error: any) {
    console.error("Error verifying payment:", error);

    let errorMessage =
      "Payment verification failed. Please contact support with your payment details.";

    if (error.name === "AbortError") {
      errorMessage =
        "Verification timed out. Your payment may still be processed. Please contact support.";
    } else if (error.response?.status === 401) {
      errorMessage =
        "Session expired during verification. Please contact support with your payment details.";
    } else if (error.message) {
      errorMessage = error.message;
    }
  }
};

export const updateCompanyPlanStatusWithTimeout = async (
  companyId: string,
  incrementDays: number
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}company/${companyId}`;

    const planExpiryDate = new Date();
    planExpiryDate.setDate(planExpiryDate.getDate() + incrementDays);

    const response = await axios.put(
      url,
      {
        planExpiry: planExpiryDate.toISOString(),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        timeout: 15000,
      }
    );

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export const updateCompanyPlanStatus = async (
  companyId: string,
  incrementDays: number
) => {
  try {
    const response = await updateCompanyPlanStatusWithTimeout(
      companyId,
      incrementDays
    );

    if (!response.data?.isSuccess) {
      throw new Error(response.data?.message || "Failed to activate account");
    }
    return response;
  } catch (error: any) {
    console.error("Error updating company plan status:", error);

    let errorMessage =
      "Failed to activate account. Your payment was successful. Please contact support.";

    if (error.name === "AbortError") {
      errorMessage =
        "Account activation timed out. Your payment was successful. Please contact support.";
    } else if (error.response?.status === 401) {
      errorMessage =
        "Session expired during activation. Your payment was successful. Please contact support.";
    }

    throw error;
  }
};
